import * as React from 'react';
import { Notify } from '@shared/ui-native/Notify';
import { latLonToEN, setMapCursor } from '@openlayers/ol-utils';
import { DateTimeSpan } from '@shared/ui-react/UtilComponents';
import { Ajax } from '@shared/Ajax';
import './LogpointsLinecodePanel.scss';
import { mapTools, mapWrapper } from '../map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { LineString } from 'ol/geom';
import OlSelect from 'ol/interaction/Select';
import * as olEventsCondition from 'ol/events/condition';
import { unByKey } from 'ol/Observable';
import * as olStyle from 'ol/style';
import { MapOverlays } from '@openlayers/ol-overlays';
import { LogpointsLinecodeService } from '@shared/logpoints/LogpointsLinecodeService';
import { alphaNumSort } from '@shared/utils/utils-sort';

export class LogpointsLinecodePanel extends React.Component<
    {
        logpoints?: ILogpoint[];
        onClose: () => void;
    },
    {}
> {
    state = {
        logpoints: this.props.logpoints,
        linecode: '',
        isLoading: false,
    };

    style = new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: [0, 0, 0, 0.7],
            width: 1.2,
            lineDash: [4, 3],
        }),
    });

    private readonly selectedStyle = new olStyle.Style({
        image: new olStyle.Circle({
            radius: 10,
            fill: new olStyle.Fill({
                color: [255, 255, 255, 0.9],
            }),
            stroke: new olStyle.Stroke({
                color: [0, 0, 0, 1],
                width: 2,
            }),
        }),
    });

    private selectClickInteraction: OlSelect;
    private mouseHoverHandler: any;
    private readonly linecodeLinesLayer = new VectorLayer({
        source: new VectorSource(),
    });

    save = () => {
        let generatePointNumbers = true;

        // check if order is changed, and point number generation is needed
        // if some dont have point number, we need to generate
        const oldLogpoints = [...this.state.logpoints];
        if (oldLogpoints.filter(l => l.hasOwnProperty('number') && l.number !== '' && l.number != null).length === oldLogpoints.length) {
            oldLogpoints.sort((a, b) => alphaNumSort(a.number, b.number));
            const oldPointOrderIds = oldLogpoints.map(l => l.id);
            const newPointOrderIds = this.state.logpoints.map(l => l.id);

            if (oldPointOrderIds.join(',') === newPointOrderIds.join(',')) {
                generatePointNumbers = false;
            }
        }

        const formData: any = {
            logpointIds: this.state.logpoints.map(l => l.id),
            linecode: this.state.linecode,
            generatePointNumbers,
        };

        if (generatePointNumbers) {
            if (confirm(__('js_map.linecode.point_numbers_will_change'))) {
                this.updateLogpoints(formData);
            }
        } else {
            this.updateLogpoints(formData);
        }
    };

    remove = () => {
        const formData: any = {
            logpointIds: this.state.logpoints.map(l => l.id),
            linecode: null,
        };

        this.updateLogpoints(formData);
    };

    close = () => {
        this.props.onClose();
    };

    clear = () => {
        this.selectClickInteraction.getFeatures().clear();
        this.linecodeLinesLayer.getSource().clear(true);
        this.setState({
            logpoints: [],
            linecode: '',
        });
    };

    componentDidMount() {
        const mapInstance = mapWrapper.getInstance();

        this.selectClickInteraction = new OlSelect({
            condition: olEventsCondition.click,
            toggleCondition: olEventsCondition.click,
            layers: [mapWrapper.featureLayers.logpoints.layer],
            filter: this.acceptFeature.bind(this),
            style: feature => [this.selectedStyle],
        });

        this.selectClickInteraction.on('select', e => {
            const logpointsData = [];
            const selected = e.target.getFeatures().forEach(f => logpointsData.push(f.get('data')));

            this.setState({
                logpoints: logpointsData,
            });
        });

        this.linecodeLinesLayer.setStyle(this.style);
        mapWrapper.addLayer(this.linecodeLinesLayer, 59, 'linecode-draw');

        mapTools.deactivateAllTools();
        mapTools.activeTool = 'logpoints-select';
        setMapCursor(mapInstance, '');

        mapInstance.removeInteraction(this.selectClickInteraction);
        mapInstance.addInteraction(this.selectClickInteraction);

        unByKey(this.mouseHoverHandler);
        this.mouseHoverHandler = mapWrapper.events.onMouseMove(evt => {
            const pixel = mapInstance.getEventPixel(evt.originalEvent);
            const feature: Feature = mapInstance.forEachFeatureAtPixel(pixel, (feature: Feature) => feature);
            if (feature && feature.get('type') == 'logpoint') {
                this.showToolTip(feature, evt);
                if (this.acceptFeature(feature)) {
                    setMapCursor(mapInstance, 'pointer');
                } else {
                    setMapCursor(mapInstance, 'not-allowed');
                }
            } else {
                setMapCursor(mapInstance, '');
            }
        });
    }

    showToolTip(feature: Feature, evt) {
        MapOverlays.hideTooltip(mapWrapper.getInstance());
        const { linecode, code, number, ...rest } = feature.get('data');
        const linecodeText = linecode ? __('js_logpoints.column_linecode') + ': ' + linecode + '<br>' : '';
        const numberText = number ? __('tablet.number_js') + ': ' + number + '<br>' : '';
        const codeText = code ? __('tablet.code_js') + ': ' + code : '';
        if (code || number || linecode) {
            MapOverlays.showTooltip(
                mapWrapper.getInstance(),
                `<div class="popup no-break-text" style="padding: 10px">${linecodeText}${numberText}${codeText}</div>`,
                evt.coordinate[0],
                evt.coordinate[1]
            );
        }
    }

    // feature can be selected if its vehicleId+source match already selected feature
    acceptFeature(feature: Feature): boolean {
        const selection = this.selectClickInteraction.getFeatures();
        if (selection.getLength() === 0) return true;

        const testFeatureData: ILogpoint = feature.get('data');
        const testFeatureSource: string = LogpointsLinecodeService.getLoadSourceAsString(feature.get('source'));
        if (!testFeatureSource) return false;

        const firstSelectedFeatureData = selection.item(0).get('data');
        const firstFeatureSource = LogpointsLinecodeService.getLoadSourceAsString(selection.item(0).get('source'));

        return testFeatureSource === firstFeatureSource && firstSelectedFeatureData.vehicleId == testFeatureData.vehicleId;
    }

    componentWillUnmount() {
        mapWrapper.getInstance().removeInteraction(this.selectClickInteraction);
        unByKey(this.mouseHoverHandler);
        mapWrapper.removeLayer(this.linecodeLinesLayer);
    }

    componentDidUpdate() {
        if (this.state.logpoints && this.state.logpoints.length) {
            this.linecodeLinesLayer.getSource().clear(true);

            const coords = this.state.logpoints.map(l => latLonToEN(l.lat, l.lon));

            const lineFeature = new Feature({
                geometry: new LineString(coords),
            });
            lineFeature.setId('linecodeLines');
            this.linecodeLinesLayer.getSource().addFeature(lineFeature);
        }
    }

    render() {
        const { logpoints } = this.state;
        const inputIsValid = logpoints && logpoints.length && this.state.linecode.length;

        return (
            <div id='logpoints-linecode-panel'>
                <header className='padded'>
                    <h2>{__('js_map.linecode.draw_line')}</h2>
                </header>

                <div>
                    {!logpoints || !logpoints.length ? (
                        <div className='padded'>
                            {__('js_map.linecode.select_logpoints')}. {__('js_map.linecode.invalid_logpoints')}.
                        </div>
                    ) : (
                        <div>
                            <div className='form-group'>
                                <label>{__('js_logpoints.column_linecode')} *</label>
                                <input
                                    type='text'
                                    value={this.state.linecode}
                                    onChange={e => {
                                        this.setState({ linecode: e.currentTarget.value });
                                    }}
                                />
                            </div>

                            <div id='logpoints-linecode-panel-list'>
                                <table className='simple-table'>
                                    <thead>
                                        <tr>
                                            <th>{__('js_map.linecode.new_point_number')}</th>
                                            <th>{__('general.date')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logpoints.map((l, i) => (
                                            <tr key={l.id}>
                                                <td style={{ width: 100 }}>{i + 1}</td>
                                                <td>
                                                    <DateTimeSpan timestamp={l.timestamp} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button onClick={this.clear} className='ik-button-secondary--subtle ik-button-small right'>
                                    {__('general.clear')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <footer>
                    <button onClick={this.remove} className='btn-danger' disabled={!(logpoints && logpoints.length) || this.state.isLoading} title={__('js_map.linecode.remove')}>
                        <i className='iki iki-cross'></i>
                    </button>

                    <div style={{ float: 'right' }} className='btn-group'>
                        <button onClick={this.close} type='button'>
                            {__('general.cancel')}
                        </button>
                        <button
                            onClick={this.save}
                            className='ik-button-primary'
                            disabled={!inputIsValid || this.state.isLoading}
                            title={!inputIsValid ? __('js_map.linecode.invalid_input') : ''}
                        >
                            {__('general.save')}
                        </button>
                    </div>
                </footer>
            </div>
        );
    }

    updateLogpoints(formData) {
        this.setState({ isLoading: true });
        Ajax({
            url: 'api/1/logpoint/linecode.json',
            method: 'POST',
            data: formData,
        })
            .always(() => this.setState({ isLoading: false }))
            .fail(Notify.error)
            .done((resp: any) => {
                const updatedLogpoints: ILogpoint[] = resp.updatedLogpoints;
                for (const l of updatedLogpoints) {
                    const existing = mapWrapper.featureLayers.logpoints.getFeatureById(l.id);
                    if (existing) {
                        existing.set('data', l);
                    }
                }
                mapWrapper.featureLayers.logpoints.onLayerSourceChanged();

                this.clear();
                Notify.success();
            });
    }
}
